import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import { ShoppingList, Product } from '../types'
import { productsApi, shoppingListsApi } from '../services/api'

const DashboardPage = () => {
  const { user } = useAuth()
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalLists: 0,
    favoriteProducts: 0,
    recentActivity: [] as string[]
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadDashboardData = async () => {
      setIsLoading(true)
      try {
        // Load products and shopping lists from API
        const [productsResponse, listsResponse] = await Promise.all([
          productsApi.getProducts(),
          shoppingListsApi.getLists()
        ])
        
        let totalProducts = 0
        let favoriteProducts = 0
        let totalLists = 0
        const recentActivity: string[] = []
        
        // Process products data
        if (productsResponse.success && productsResponse.data) {
          const products = Array.isArray(productsResponse.data) ? productsResponse.data : []
          totalProducts = products.length
          favoriteProducts = products.filter((p: Product) => p.isFavorite).length
          
          // Add recent products to activity (newest first)
          products.slice(-4).reverse().forEach((product: Product) => {
            recentActivity.push(`Tuote: ${product.name}`)
          })
        }
        
        // Process shopping lists data
        if (listsResponse.success && listsResponse.data) {
          const lists = Array.isArray(listsResponse.data) ? listsResponse.data : []
          totalLists = lists.length
          
          // Add recent lists to activity
          lists.slice(-2).reverse().forEach((list: ShoppingList) => {
            recentActivity.push(`Lista: ${list.name}`)
          })
        }
        
        setStats({
          totalProducts,
          totalLists,
          favoriteProducts,
          recentActivity: recentActivity.slice(0, 4) // Show only 4 most recent
        })
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
        // Fallback to basic stats
        setStats({
          totalProducts: 0,
          totalLists: 0,
          favoriteProducts: 0,
          recentActivity: ['Virhe ladattaessa dataa']
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadDashboardData()
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Tervetuloa, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="text-gray-600">
          Tässä on yhteenveto ostoslistojen tilanteesta
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div className="ml-4">
              <h2 className="text-lg font-semibold text-gray-900">{stats.totalProducts}</h2>
              <p className="text-sm text-gray-600">Tuotetta yhteensä</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="ml-4">
              <h2 className="text-lg font-semibold text-gray-900">{stats.totalLists}</h2>
              <p className="text-sm text-gray-600">Ostolistaa</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <div className="ml-4">
              <h2 className="text-lg font-semibold text-gray-900">{stats.favoriteProducts}</h2>
              <p className="text-sm text-gray-600">Suosikkituotetta</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Pikatoiminnot</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/products"
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors group"
          >
            <div className="text-center">
              <svg className="w-8 h-8 text-gray-400 group-hover:text-primary-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="text-sm font-medium text-gray-900 group-hover:text-primary-700">
                Lisää tuote
              </span>
            </div>
          </Link>

          <Link
            to="/shopping-lists"
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors group"
          >
            <div className="text-center">
              <svg className="w-8 h-8 text-gray-400 group-hover:text-primary-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span className="text-sm font-medium text-gray-900 group-hover:text-primary-700">
                Uusi lista
              </span>
            </div>
          </Link>

          <Link
            to="/products?favorites=true"
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors group"
          >
            <div className="text-center">
              <svg className="w-8 h-8 text-gray-400 group-hover:text-primary-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <span className="text-sm font-medium text-gray-900 group-hover:text-primary-700">
                Suosikit
              </span>
            </div>
          </Link>

          <Link
            to="/shopping-lists"
            className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors group"
          >
            <div className="text-center">
              <svg className="w-8 h-8 text-gray-400 group-hover:text-primary-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <span className="text-sm font-medium text-gray-900 group-hover:text-primary-700">
                Kauppaan
              </span>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Viimeaikainen toiminta</h2>
        <div className="space-y-3">
          {stats.recentActivity.map((activity, index) => (
            <div key={index} className="flex items-center text-sm text-gray-600">
              <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
              {activity}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default DashboardPage 